import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ColorPalette, defaultPalette, applyPalette } from '../theme';
import { SITE_NAME } from '../brand';

export interface SiteSettings {
  id: string;
  companyName: string;
  logoUrl: string;
  bannerImage: string;
  description: string;
  catalogLabel: string;
  email: string;
  phone: string;
  address: string;
  socialMedia: {
    facebook: string;
    instagram: string;
  };
  colors: ColorPalette;
  updatedAt?: Date;
}

const defaultSettings: SiteSettings = {
  id: 'site-settings',
  companyName: SITE_NAME,
  logoUrl: '',
  bannerImage: '',
  description: 'Апартаменти и офиси за продажба',
  catalogLabel: 'Продажби',
  email: '',
  phone: '',
  address: '',
  socialMedia: {
    facebook: '',
    instagram: ''
  },
  colors: defaultPalette
};

export const useSettings = () => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'content', 'site-settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const merged = {
          id: docSnap.id,
          ...data,
          // Fall back for docs saved before these fields existed.
          logoUrl: data.logoUrl ?? '',
          bannerImage: data.bannerImage ?? '',
          description: data.description ?? defaultSettings.description,
          catalogLabel: data.catalogLabel ?? defaultSettings.catalogLabel,
          // Merge colors with defaults so documents saved before this feature
          // existed (or partial palettes) still render every color.
          colors: { ...defaultPalette, ...(data.colors || {}) },
          updatedAt: data.updatedAt?.toDate()
        } as SiteSettings;
        setSettings(merged);
        applyPalette(merged.colors);
        // Keep the browser tab in sync with the project name from the database,
        // so a cloned site is correctly titled without touching index.html.
        if (merged.companyName) {
          document.title = merged.description
            ? `${merged.companyName} - ${merged.description}`
            : merged.companyName;
        }
      } else {
        // Create default settings if they don't exist
        await saveSettings(defaultSettings);
        setSettings(defaultSettings);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Грешка при зареждане на настройките');
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: Partial<SiteSettings>) => {
    try {
      const docRef = doc(db, 'content', 'site-settings');
      const settingsData = {
        ...newSettings,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(docRef, settingsData);
      setSettings({ ...settings, ...newSettings, updatedAt: new Date() });
      return true;
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Грешка при запазване на настройките');
      return false;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    settings,
    loading,
    error,
    saveSettings,
    refreshSettings: fetchSettings
  };
};